import { useState } from "react";
import { advisoryBoardSchema, type AdvisoryBoardPayload } from "@anton/shared";
import { PageHeader } from "../components/PageHeader";
import { Field } from "../components/Field";
import { SubmitButton } from "../components/SubmitButton";
import { SuccessScreen } from "../components/SuccessScreen";
import { Card, Lead, P, H3, CheckList, CrossList, SectionHeader, Stat } from "../components/Section";
import { submitApplication } from "../lib/api";
import { haptic, showAlert } from "../lib/telegram";

export default function AdvisoryBoard() {
  const [form, setForm] = useState<Partial<AdvisoryBoardPayload>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (done) {
    return <SuccessScreen message="Заявка ушла мне лично. Свяжусь с тобой сам." />;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = advisoryBoardSchema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const issue of parsed.error.issues) fe[issue.path.join(".")] = issue.message;
      setErrors(fe);
      haptic("error");
      return;
    }
    setErrors({});
    setLoading(true);
    const res = await submitApplication("ADVISORY_BOARD", parsed.data);
    setLoading(false);
    if (!res.ok) {
      showAlert(res.error ?? "Ошибка отправки");
      haptic("error");
      return;
    }
    setDone(true);
  }

  return (
    <div className="pb-10">
      <PageHeader
        eyebrow="Совет директоров на аутсорсе"
        title="Advisory Board"
        subtitle="Внешний адвайзер для собственника. Растём быстрее за счёт правильных гипотез и регулярного контроля."
        icon="💼"
        iconColor="indigo"
      />

      <div className="px-5 mt-3 space-y-3">
        <Card>
          <Lead>
            Это не обучение, не наставничество и не «поговорить за бизнес».
            Это система: цель → гипотезы → приоритет → действия → результат.
          </Lead>
        </Card>

        {/* Формат */}
        <Card>
          <H3>Как мы работаем</H3>
          <P>
            Раз в неделю — трекшн-сессия 1–1,5 часа. Между встречами — чат для коротких вопросов и контроля внедрения.
            Я не подменяю собой команду — я держу фокус собственника на главном.
          </P>
        </Card>

        {/* Цифровая раскладка */}
        <div className="grid grid-cols-3 gap-3">
          <Stat value="4" label="встречи в месяц" />
          <Stat value="3" label="гипотезы в неделю" />
          <Stat value="1" label="ключевая на неделю" />
        </div>

        <Card>
          <H3>Что получаешь каждую неделю</H3>
          <CheckList items={[
            "Разбор недели по цифрам, а не по ощущениям",
            "Список бесплатных и дешёвых гипотез — корректно сформулированных",
            "Понимание, на какую метрику работает каждая гипотеза",
            "Чёткие приоритеты — что делать в первую очередь, а что не трогать вообще",
            "Конкретный план действий с задачами под тест",
          ]} />
        </Card>

        <Card>
          <H3>Бонусы (по согласованию)</H3>
          <CheckList items={[
            "Реклама в моём Instagram (10 000+ просмотров сторис)",
            "Использование моего лица как части твоей команды",
            "Доступ к моей записной книжке — контакты, которые я собирал 18 лет",
            "Готовые регламенты, шаблоны, системы из моих бизнесов",
          ]} />
        </Card>

        <Card>
          <H3>Кому подойдёт</H3>
          <CheckList items={[
            "Бизнес уже работает, есть выручка и команда",
            "Готовность внедрять, а не обсуждать",
            "Чувствуешь, что застрял — давно нет больших изменений",
          ]} />
        </Card>

        <Card>
          <H3>Кому НЕ подойдёт</H3>
          <CrossList items={[
            "Нет бизнеса или выручки",
            "Хочешь «магию» и мотивацию",
            "Нет времени делать — нужен «консультант на поговорить»",
          ]} />
        </Card>

        <div className="ios-callout">
          🔥 Сейчас доступно <b>всего 2 места</b>. Беру ограниченное количество клиентов, чтобы сохранять качество работы.
        </div>

        <div className="ios-callout-muted">
          Стоимость обсуждается индивидуально по заявке.
        </div>
      </div>

      <SectionHeader>Оставить заявку</SectionHeader>
      <form noValidate onSubmit={onSubmit} className="px-5 space-y-4">
        <Field label="Как тебя зовут?" error={errors["name"]}>
          <input className={`ios-input ${errors["name"] ? "ios-input-error" : ""}`}
            value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Имя" />
        </Field>
        <Field label="Контакт (Telegram или телефон)" error={errors["contact"]}>
          <input className={`ios-input ${errors["contact"] ? "ios-input-error" : ""}`}
            value={form.contact ?? ""} onChange={(e) => setForm({ ...form, contact: e.target.value })}
            placeholder="@username или +7…" />
        </Field>
        <Field label="Ниша бизнеса и месячный оборот" error={errors["business"]}>
          <input className={`ios-input ${errors["business"] ? "ios-input-error" : ""}`}
            value={form.business ?? ""} onChange={(e) => setForm({ ...form, business: e.target.value })}
            placeholder="например: edtech, 5 млн ₽/мес" />
        </Field>
        <Field label="Цель и запрос к борду" error={errors["goal"]}>
          <textarea rows={4} className={`ios-input ${errors["goal"] ? "ios-input-error" : ""}`}
            value={form.goal ?? ""} onChange={(e) => setForm({ ...form, goal: e.target.value })}
            placeholder="Что хочешь получить от работы со мной?" />
        </Field>
        <div className="pt-2">
          <SubmitButton loading={loading}>Отправить заявку</SubmitButton>
        </div>
      </form>
    </div>
  );
}
